import { useMutation } from '@apollo/client';
import { transformError } from '@core/adapters/errors/transformError';
import { useCallback } from 'react';

import { COMPLETE_AUDIT_SESSION } from '../graphql/mutations/completeAuditSession';

interface CompleteAuditSessionResponse {
  completeAuditSession: {
    id: string;
    name: string;
    status: string;
    completedAt: string;
    updatedAt?: string;
    updatedBy?: string;
  };
}

interface CompleteAuditSessionVariables {
  sessionId: string;
}

/**
 * Hook for completing an audit session
 */
export function useCompleteAuditSession() {
  const [completeAuditSessionMutation, { loading }] = useMutation<CompleteAuditSessionResponse, CompleteAuditSessionVariables>(COMPLETE_AUDIT_SESSION, {
    errorPolicy: 'all',
  });

  const completeAuditSession = useCallback(
    async (auditSessionId: string) => {
      try {
        const result = await completeAuditSessionMutation({
          variables: {
            sessionId: auditSessionId,
          },
        });

        if (result.errors) {
          const adaptedErrors = result.errors.map(error => transformError({ graphQLErrors: [error] }));

          return {
            success: false,
            errors: adaptedErrors,
          };
        }

        return {
          success: true,
          data: result.data?.completeAuditSession,
        };
      } catch (error) {
        const adaptedError = transformError(error);

        return {
          success: false,
          errors: [adaptedError],
        };
      }
    },
    [completeAuditSessionMutation]
  );

  return {
    completeAuditSession,
    loading,
  };
}
